import { useEffect, useMemo, useRef } from 'react';
import { shallowEqual } from 'react-redux';
import useSWR from 'swr';
import { getUserFavorites } from '@surfline/web-common';

import { useAppSelector } from 'stores/hooks';
import type { UserFavorites } from 'types/userFavorites';
import favoritesFetch from 'utils/favoritesFetch';

export type Region = { forecastStatus: 'active' | 'inactive'; id: string; name: string };

const useFavorites = ({
  allRegions = false,
  includeInternalCams = false,
  isInternalUser = false,
}: {
  allRegions?: boolean;
  includeInternalCams?: boolean;
  isInternalUser?: boolean;
}): { favorites: UserFavorites; regions: Array<Region> } => {
  const reduxFavorites = useAppSelector(getUserFavorites, shallowEqual) as UserFavorites;
  const usingSWRPath = isInternalUser && includeInternalCams;

  const { data: internalFavData, mutate } = useSWR(
    usingSWRPath ? '/kbyg/favorites?includeInternalCams=true' : null,
    () => favoritesFetch(false, true),
    { revalidateOnFocus: false, revalidateOnReconnect: false, errorRetryCount: 0 },
  );

  const prevReduxLengthRef = useRef(reduxFavorites?.length);
  useEffect(() => {
    if (!usingSWRPath) return;
    if (prevReduxLengthRef.current !== reduxFavorites?.length) {
      mutate();
    }
    prevReduxLengthRef.current = reduxFavorites?.length;
  }, [usingSWRPath, reduxFavorites, mutate]);

  const favorites = useMemo(() => {
    if (isInternalUser && internalFavData?.data?.favorites) {
      return internalFavData.data.favorites;
    }
    return reduxFavorites;
  }, [isInternalUser, internalFavData, reduxFavorites]);

  const regions: Array<Region> = useMemo(
    () =>
      favorites?.reduce<Array<Region>>((acc, fav) => {
        if (!acc.some((region) => region.id === fav.subregionId)) {
          acc.push({
            id: fav.subregionId,
            name: fav.subregionName,
            forecastStatus: fav?.forecastStatus ?? 'inactive',
          });
        }
        return acc;
      }, []),
    [favorites],
  );

  return {
    favorites,
    regions: allRegions
      ? [{ forecastStatus: 'inactive', id: 'all', name: 'All' }, ...regions]
      : regions,
  };
};

export default useFavorites;
